import React, { useEffect, useRef, useState } from 'react';
import { AvKeyValue, AvKeyValueProps, AvTooltip } from '@fabric-ui/components';
import { findTextRanges } from '../KeyValueExplorer/KeyValueExplorer.highlight.utils';
import {
  countVisibleKvSpans,
  EllipsisIndicator,
  hasTermMatch,
  LINE_HEIGHT_PX,
  registerHighlightRanges,
  scheduleAfterLayoutSettles,
  tooltipPopperProps,
  unregisterHighlightRanges,
} from './AvKeyValueWithTooltip.utils';

export const AvKeyValueWithTooltip: React.FC<AvKeyValueProps> = ({ linesLimit = 2, ...rest }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // Stable identity key for this instance in the shared highlight registry
  const instanceKey = useRef({});
  const [visibleLimit, setVisibleLimit] = useState<number | null>(null);

  const { value, highlightText, ...keyValueProps } = rest;
  const totalKeys = Object.keys(value ?? {}).length;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const maxHeightPx = LINE_HEIGHT_PX * linesLimit;

    const measureAndUpdateVisibleLimit = () => {
      const visibleCount = countVisibleKvSpans(container, maxHeightPx);
      setVisibleLimit(visibleCount < totalKeys ? visibleCount : null);
    };

    const cancelScheduled = scheduleAfterLayoutSettles(measureAndUpdateVisibleLimit);
    if (typeof ResizeObserver === 'undefined') {
      return () => cancelScheduled();
    }

    const obs = new ResizeObserver(measureAndUpdateVisibleLimit);
    obs.observe(container);

    return () => {
      cancelScheduled();
      obs.disconnect();
    };
  }, [linesLimit, totalKeys, value]);

  useEffect(() => {
    const container = containerRef.current;
    const term = highlightText?.[0]?.trim().toLowerCase();
    const key = instanceKey.current;
    const ranges = term && container ? findTextRanges(container, term, { caseInsensitive: true }) : [];
    registerHighlightRanges(key, ranges);
    return () => unregisterHighlightRanges(key);
  }, [highlightText, visibleLimit]);

  const isClipped = visibleLimit !== null;
  const hiddenEntries = isClipped ? Object.entries(value ?? {}).slice(visibleLimit!) : [];
  const showHiddenMatchIndicator = isClipped && hasTermMatch(hiddenEntries, highlightText ?? []);

  return (
    <div ref={containerRef}>
      <AvKeyValue customEllipsis linesLimit={linesLimit} limit={Math.max(1, visibleLimit ?? totalKeys)} value={value} {...keyValueProps}>
        {isClipped && (
          <AvTooltip
            title={<AvKeyValue limit={-1} value={value} highlightText={highlightText} {...keyValueProps} />}
            variant="message"
            color="white"
            popperProps={tooltipPopperProps}
            disableInteractive={false}>
            <EllipsisIndicator highlighted={showHiddenMatchIndicator} />
          </AvTooltip>
        )}
      </AvKeyValue>
    </div>
  );
};
